<template>
  <div class="subprogram-nba-schedule-table">
    <el-table
      ref="table"
      row-key="id"
      v-loading="loading"
      :data="matches"
      @sort-change="doChangeSort({subpropertyId: subpropertyId, programId: programIdm, subprogramId: subprogramId})"
    >
      <!-- Date and hour -->
      <el-table-column
        :label="fields.timestamp.label" :prop="fields.timestamp.name"
        class="subprogram-nba-schedule-table__column"
        min-width="170"
        >
        <template 
          slot-scope="scope"
          class="subprogram-nba-schedule-table__column__date"
        >
          <span
            class="subprogram-nba-schedule-table__column__date__text"
            v-text="scope.row.date+' - '+scope.row.hour"
          />
        </template>
      </el-table-column>
      
      <!-- Status -->
      <el-table-column
        :label="fields.status.label"
        :prop="fields.status.name"
        min-width="150"
      >
        <template slot-scope="scope">
          <div class="subprogram-nba-schedule-table__status">
            <i
              :style="getTagStyle('status', scope.row.status)"
              class="icon-radio-button-fill"
            />
            <span v-text="presenter(scope.row, 'status')" />
          </div>
        </template>
      </el-table-column>

      <!-- Rounds -->
      <el-table-column
        v-if="isRegularSeasonHasRounds"
        :label="i18n('entities.subprogram.nba.rounds.name')"
        min-width="90"
        align="center"
      >
        <template slot-scope="scope">
          <span v-text="get(scope.row, 'regularSeasonRound.order', '-')"/>
        </template>
      </el-table-column>

      <!-- Type -->
      <el-table-column
        :label="fields.type.label"
        min-width="90"
        align="center"
      >
        <template slot-scope="scope">
          <span v-text="presenter(scope.row, 'type')"/>
        </template>
      </el-table-column>

      <!-- Playoff -->
      <el-table-column
        :label="i18n('entities.subprogram.nba.playoffs.name')"
        min-width="120"
        align="center"
      >
        <template slot-scope="scope">
          <span v-text="get(scope.row, `playoff.nameI18n.${languageCode}`, '-') + ' ' + get(scope.row, `playoff.numGame`, '')"/>
        </template>
      </el-table-column>

      <!-- Teams -->
      <el-table-column
        :label="i18n('entities.team.label.many')"
        class="subprogram-nba-schedule-table__column"
        min-width="280"
      >
        <template slot-scope="scope">   
          <div
            class="subprogram-nba-schedule-table__column__teams"
          >
            <!-- Home team -->
            <div
              class="subprogram-nba-schedule-table__column__teams__item"
            >
              <!-- Logo -->
              <div
                class="subprogram-nba-schedule-table__column__teams__item__logo"
                :style="{ backgroundImage: `url(${get(scope.row, 'homeTeam.logo[0].thumbPublicUrl', '')})`}"
              />

              <!-- Name -->
              <span 
                v-if="scope.row.homeTeam.name"
                class="subprogram-nba-schedule-table__column__teams__item__name"
                v-text="get(scope.row, 'homeTeam.name', '-')" 
              />
              <span 
                v-else-if="scope.row.homeTeam.placeholderI18n"
                class="subprogram-nba-schedule-table__column__teams__item__name"
                v-text="get(scope.row, `homeTeam.placeholderI18n.${languageCode}`, '-')" 
              />

              <!-- School name -->
              <span
                class="subprogram-nba-schedule-table__column__teams__item__school"
                v-text="get(scope.row, 'homeTeam.school.name', i18n('entities.game.list.empty.school'))" 
              />
            </div>

            <!-- Away team -->
            <div
              class="subprogram-nba-schedule-table__column__teams__item"
            >
              <!-- Logo -->
              <div
                class="subprogram-nba-schedule-table__column__teams__item__logo"
                :style="{ backgroundImage: `url(${get(scope.row, 'awayTeam.logo[0].thumbPublicUrl', '')})`}"
              />

             <!-- Name -->
              <span 
                v-if="scope.row.awayTeam.name"
                class="subprogram-nba-schedule-table__column__teams__item__name"
                v-text="get(scope.row, 'awayTeam.name', '-')" 
              />
              <span 
                v-else-if="scope.row.awayTeam.placeholderI18n"
                class="subprogram-nba-schedule-table__column__teams__item__name"
                v-text="get(scope.row, `awayTeam.placeholderI18n.${languageCode}`, '-')" 
              />


              <!-- School name -->
              <span 
                class="subprogram-nba-schedule-table__column__teams__item__school"
                v-text="get(scope.row, 'awayTeam.school.name',  i18n('entities.game.list.empty.school'))" 
              />
            </div>
          </div>
        </template>
      </el-table-column>

      <!-- Results -->
      <el-table-column
        :label="fields.results.label"
        min-width="110"
        class="subprogram-nba-schedule-table__column"
        align="center"
      >
        <template slot-scope="scope">
            <!-- Home score -->
            <span 
              class="subprogram-nba-schedule-table__column__home__score"
              v-text="scope.row.homeTeamScore ? scope.row.homeTeamScore : '-'"
            />
            <!-- Away score -->
            <span 
              class="subprogram-nba-schedule-table__column__away__score"
              v-text="scope.row.awayTeamScore ? scope.row.awayTeamScore : '-'"
            />
        </template>
      </el-table-column>
    
      <!-- Location -->
      <el-table-column
        :label="fields.location.label"
        :prop="fields.location.name"
        class="subprogram-nba-schedule-table__column"
        min-width="140"
      >
        <template slot-scope="scope"> 
          <span
            class="subprogram-nba-schedule-table__column__location"
            v-text="get(scope.row, 'location.name',  i18n('common.tbd'))"
          />
        </template>
      </el-table-column>

      <!-- Actions -->
      <el-table-column :fixed="isMobile? undefined : 'right'" align="center" min-width="80">
        <template slot-scope="scope">
          <div class="table-actions">

            <!-- Edit -->
            <router-link 
              v-if="hasPermissionToEdit"
              :to="{
                name: 'gameEdit',
                params: {
                  id: scope.row.id,
                  programId: programId,
                  subprogramId: subprogramId,
                }
              }" 
            >
              <el-button
                icon="icon-pen"
                circle
              />
            </router-link>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div class="el-pagination-wrapper">
      <el-pagination
        :current-page="pagination.currentPage || 1"
        :disabled="loading"
        :layout="paginationLayout"
        :total="count"
        @current-change="(currentPage) => doChangePaginationCurrentPage({subpropertyId: subpropertyId, programId: programId, subprogramId: subprogramId, currentPage})"
        @size-change="(pageSize) => doChangePaginationPageSize({subpropertyId: subpropertyId, programId: programId, subprogramId: subprogramId, pageSize})"
      ></el-pagination>
    </div>

  </div>
</template>

<script>
import { GameModel } from '@modules/game/game-model';
import { mapActions, mapGetters } from 'vuex';
import { GamePermissions } from '@modules/game/game-permissions';
import { countBy, get } from 'lodash';
import { i18n, getLanguageCode } from '@shared/i18n';
import TagsFactory from '@shared/factories/tags-factory';

  export default {
    name: 'app-subprogram-nba-schedule-table',
    props: {
      programId: {
        type: String,
      },
      subprogramId: {
        type: String,
      },
    },

    computed: {
      ...mapGetters({ 
        isMobile: 'layout/isMobile',
        rowSpanPerIndex: 'subprogram/nba/schedule/rowSpanPerIndex',
        matches: 'subprogram/nba/schedule/matches',
        loading: 'subprogram/nba/schedule/loading',
        count: 'subprogram/nba/schedule/count',
        pagination: 'subprogram/nba/schedule/pagination',
        paginationLayout: 'layout/paginationLayout',
        currentManage: 'auth/manage/currentManage',
        currentUser: 'auth/currentUser',
        dateFormat: 'auth/manage/dateFormat',
        timeFormat: 'auth/manage/timeFormat',
        subprogram: 'subprogram/nba/subprogram',
      }),
      
      hasPermissionToEdit() {
        return new GamePermissions(this.currentUser).edit;
      },

      numRowSpanDate() {
        return  get(countBy(this.matches, 'date'), 'null', 0);
      },

      subpropertyId() {
        return get(this, 'currentManage.subproperty.id', null);
      },

      fields() {
        const { dateFormat, timeFormat } = this;
        return new GameModel({ dateFormat, timeFormat }).fields();
      },

      isRegularSeasonHasRounds() {
        return get(this, 'subprogram.regularSeasonHasRounds', false); 
      },

      languageCode() {
        return getLanguageCode()
      }
      
      // regularSeasonRound() {
      //   return this.matches.map(({regularSeasonRound}) => regularSeasonRound);
      // }
    },

    mounted() {
      // this.doFetch({ 
      //   subpropertyId: this.subpropertyId, 
      //   subprogramId: this.subprogramId 
      // });
    },

    methods: {
      ...mapActions({
        doChangeSort: 'subprogram/nba/schedule/doChangeSort',
        doChangePaginationCurrentPage: 'subprogram/nba/schedule/doChangePaginationCurrentPage',
        doChangePaginationPageSize: 'subprogram/nba/schedule/doChangePaginationPageSize',
        doFetch: 'subprogram/nba/schedule/doFetch'
      }),

      // presenter(row, fieldName) {
      //   return GameModel.presenter(row, fieldName);
      // },

      presenter(row, fieldName) {
        const { dateFormat, timeFormat } = this;
        return new GameModel({ dateFormat, timeFormat }).presenter(row, fieldName);
      },

      i18n(code) {
        return i18n(code);
      },

      get(obj, key, def) {
        return get(obj, key, def)
      },
      // Table methods 

      /**
       * method that returns rowspan and colspan
       * params : { row, column, rowIndex, columnIndex }
       */
      objectSpanMethod({ rowIndex, columnIndex }) {
        if (columnIndex === 0) {

          if (this.rowSpanPerIndex[rowIndex] !== undefined) {
            
            return {
              rowspan: this.rowSpanPerIndex[rowIndex] ,
              colspan: 1,
            };
          } else {
            return {
              rowspan: 0,
              colspan: 0,
            };
          }
        }
      },

      getTagStyle(keyEnum, valueEnum) {
        const style = new TagsFactory({
          entityName: 'gameStatus',
        }).getTagStyle({
          keyEnum: keyEnum,
          valueEnum: valueEnum,
        });
        return style;
      },
    },
  }
</script>

<style lang="scss" scoped>
  .subprogram-nba-schedule-table {
    .el-table {
      margin-top: 20px;
    }
    &__column {
      &__date {
        display: block;
        &__text {
          display: block;
          color: '#979797';
          font-size: 14px;
          line-height: 1.21;
          letter-spacing: 0.2px;
        }
      }
      &__teams {
        display: grid;
        grid-row-gap: 12px;
        
        &__item {
          display: grid;
          grid-template-rows: 20px auto;
          grid-template-columns: 40px auto;
          grid-template-areas:  'logo name' 
                                'logo school';
          grid-column-gap: 10px;
          &__logo {
            grid-area: logo;
            width: 40px;
            height: 40px;
            background-size: contain;
            background-position: center;
            background-repeat: no-repeat;
            // border-radius: 100%;
          }
          &__name {
            grid-area: name;
            display: block;
            color: rgba(102, 102, 102, 0.74);
            font-size: 14px;
            text-transform: uppercase;
            font-weight: 600;
            height: 20px;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          &__school {
            grid-area: school;
            display: block;
            color: #979797;
            height: 12px;
          }
        }
      }
      &__home__score {
        display: grid;
        font-weight: 600;
        height: 46px;
        align-items: center;
      }
      &__away__score {
        display: grid;
        font-weight: 600;
        height: 46px;
        align-items: center;
      }

      &__location {
        display: flex;
        overflow: hidden; 
        text-overflow: ellipsis;
      }
    }
    &__status {
      display: grid;
      grid-template-areas: 'icon status';
      justify-content: flex-start;
      align-items: center;
      background-color: #fff;
      box-shadow: 0 2px 5px 0 rgba(223, 223, 223, 0.7);
      border-radius: 5px;
      width: 150px;
      padding: 8px;
      gap: 8px;
    }
  }
</style>
<style>
  .subprogram-nba-schedule-table .el-table__row {
    cursor: default;
  }
</style>